<template>
  <div class="bg-n-slate-2 dark:bg-n-slate-3 rounded-xl shadow-sm border border-n-weak p-6 flex flex-col" :style="{ minHeight: chartHeight }">
    <h3 class="text-lg font-semibold text-n-slate-12 mb-6">
      ⚡ Taxa de Resolução por Agente (Top 10)
    </h3>
    <div v-if="data && data.length > 0" class="relative flex-1 min-h-0">
      <canvas ref="chartCanvas"></canvas>
    </div>
    <div v-else class="text-center py-8">
      <p class="text-n-slate-10">
        Nenhum dado de performance disponível
      </p>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export default {
  name: 'PerformanceChart',
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    selectedAgentName: {
      type: String,
      default: null,
    },
  },
  emits: ['agent-click'],
  setup(props, { emit }) {
    const chartCanvas = ref(null);
    let chartInstance = null;

    const isDarkMode = computed(() => {
      if (typeof window === 'undefined') return false;
      return document.documentElement.classList.contains('dark');
    });

    const chartHeight = computed(() => {
      const agentCount = props.data?.length || 0;
      if (agentCount === 0) return '400px';
      
      // 50px por agente + 200px de padding/header
      const calculatedHeight = agentCount * 50 + 200;
      const finalHeight = Math.min(Math.max(calculatedHeight, 400), 1200);
      
      return `${finalHeight}px`;
    });

    const createChart = async () => {
      await nextTick();
      
      if (!chartCanvas.value) {
        console.warn('⚠️ PerformanceChart - Canvas not found');
        return;
      }

      if (!props.data || props.data.length === 0) {
        console.warn('⚠️ PerformanceChart - No data to display');
        return;
      }

      if (chartInstance) {
        chartInstance.destroy();
      }

      const ctx = chartCanvas.value.getContext('2d');

      const sortedData = [...props.data].sort((a, b) => b.resolution_rate - a.resolution_rate);

      const getBarColors = () => {
        return sortedData.map(item => {
          const baseColor = 'rgba(59, 130, 246, 0.8)';
          const highlightColor = 'rgba(59, 130, 246, 1)';
          const dimColor = 'rgba(59, 130, 246, 0.3)';

          if (!props.selectedAgentName) return baseColor;

          if (item.name === props.selectedAgentName) return highlightColor;

          return dimColor;
        });
      };

      const getBorderColors = () => {
        return sortedData.map(item => {
          if (!props.selectedAgentName) return 'rgba(59, 130, 246, 1)';
          if (item.name === props.selectedAgentName) return 'rgba(37, 99, 235, 1)';
          return 'rgba(59, 130, 246, 0.5)';
        });
      };

      const getBorderWidths = () => {
        return sortedData.map(item => {
          if (!props.selectedAgentName) return 2;
          return item.name === props.selectedAgentName ? 3 : 1;
        });
      };

      const gridColor = isDarkMode.value 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'rgba(0, 0, 0, 0.05)';

      const textColor = isDarkMode.value 
        ? 'rgb(203, 213, 225)'
        : 'rgb(71, 85, 105)';

      const tooltipBg = isDarkMode.value 
        ? 'rgba(15, 23, 42, 0.95)' 
        : 'rgba(255, 255, 255, 0.95)';

      const tooltipBorder = isDarkMode.value 
        ? 'rgba(71, 85, 105, 0.5)' 
        : 'rgba(203, 213, 225, 0.5)';

      chartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: sortedData.map(item => item.name),
          datasets: [
            {
              label: 'Taxa de Resolução (%)',
              data: sortedData.map(item => item.resolution_rate),
              backgroundColor: getBarColors(),
              borderColor: getBorderColors(),
              borderWidth: getBorderWidths(),
              borderRadius: 8,
              barPercentage: 0.7,
              categoryPercentage: 0.8,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y',
          layout: {
            padding: {
              bottom: 10,
            },
          },
          onClick: (event, elements) => {
            if (elements.length > 0) {
              const index = elements[0].index;
              const agentData = sortedData[index];
              emit('agent-click', agentData.name);
            }
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              backgroundColor: tooltipBg,
              titleColor: textColor,
              bodyColor: textColor,
              borderColor: tooltipBorder,
              borderWidth: 1,
              padding: 12,
              displayColors: true,
              callbacks: {
                label: function (context) {
                  const agent = sortedData[context.dataIndex];
                  return [
                    `Taxa de Resolução: ${context.parsed.x}%`,
                    `Total Atendidas: ${agent.total_handled}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              position: 'bottom',
              beginAtZero: true,
              max: 100,
              ticks: {
                color: textColor,
                stepSize: 20,
                padding: 5,
                callback: function (value) {
                  return value + '%';
                },
              },
              grid: {
                color: gridColor,
                drawBorder: true,
                drawOnChartArea: true,
                borderColor: gridColor,
                borderWidth: 2,
              },
            },
            y: {
              ticks: {
                color: textColor,
                callback: function(value, index) {
                  const label = this.getLabelForValue(value);
                  if (label.length > 20) {
                    return label.substring(0, 17) + '...';
                  }
                  return label;
                },
              },
              grid: {
                display: false,
              },
              border: {
                color: gridColor,
              },
            },
          },
        },
      });
    };

    const observer = new MutationObserver(() => {
      createChart();
    });

    watch(() => props.data, () => {
      createChart();
    }, { deep: true });

    watch(() => props.selectedAgentName, () => {
      createChart();
    });

    onMounted(() => {
      createChart();
      
      if (typeof window !== 'undefined') {
        observer.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    });

    onUnmounted(() => {
      if (chartInstance) {
        chartInstance.destroy();
      }
      observer.disconnect();
    });

    return {
      chartCanvas,
      chartHeight,
    };
  },
};
</script>